
import { useTranslation } from 'react-i18next';

export default function FeaturesSection() {
  const { t } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      console.warn(`Element with id '${sectionId}' not found`);
      return;
    }
    element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="features" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-indigo-400 text-xl mb-3">{t('featuresSubtitle')}</h2>
          <h3 className="text-4xl font-bold text-white">{t('featuresTitle')}</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Feature 1 */}
          <div className="bg-gray-900 rounded-xl p-8 hover:bg-gray-800 transition-all duration-300 hover:scale-105">
            <h4 className="text-2xl font-bold mb-4 text-white">{t('feature1Title')}</h4>
            <p className="text-gray-300 mb-6">
              {t('feature1Description')}
            </p>

            <div className="bg-gray-800 rounded-lg p-6 mb-4">
              <div className="flex items-center justify-between mb-6">
                <h5 className="font-medium text-white">{t('translationTimeComparison')}</h5>
                <span className="text-xs text-gray-400">{t('basedOnProject')}</span>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300">{t('traditionalAgency')}</span>
                    <span className="text-sm text-gray-400">{t('10days')}</span>
                  </div>
                  <div className="h-8 bg-gray-700/50 rounded-lg overflow-hidden">
                    <div className="h-full w-full bg-gray-600/50 rounded-lg flex items-center px-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-time-line text-gray-400"></i>
                        <span className="text-xs text-gray-400">{t('240hours')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300">{t('aiPlatform')}</span>
                    <span className="text-sm text-green-400">{t('1day')}</span>
                  </div>
                  <div className="h-8 bg-gray-700/50 rounded-lg overflow-hidden">
                    <div className="h-full w-[10%] bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center px-3">
                      <div className="flex items-center gap-2 w-full">
                        <i className="ri-flashlight-line text-white"></i>
                        <span className="text-xs text-white whitespace-nowrap">{t('24hours')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center text-indigo-400 hover:text-purple-400 transition-colors cursor-pointer"
            >
              {t('contactUs')}
              <i className="ri-arrow-right-line ri-sm ml-2"></i>
            </button>
          </div>

          {/* Feature 2 */}
          <div className="bg-gray-900 rounded-xl p-8 hover:bg-gray-800 transition-all duration-300 hover:scale-105">
            <h4 className="text-2xl font-bold mb-4 text-white">{t('feature2Title')}</h4>
            <p className="text-gray-300 mb-6">
              {t('feature2Description')}
            </p>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <div className="h-48 overflow-hidden rounded-lg mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20gaming%20translation%20interface%20with%20game%20dialogue%20boxes%20showing%20different%20languages%2C%20RPG%20fantasy%20adventure%20game%20screenshots%2C%20colorful%20game%20UI%20elements%2C%20fantasy%20medieval%20setting%20with%20warriors%20and%20magic&width=600&height=300&seq=gameui&orientation=landscape"
                  alt="Game Translation Interface"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <i className="ri-gamepad-line text-blue-500"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-white">{t('glossary')}</span>
                      <p className="text-xs text-gray-400">{t('gameTermsReference')}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">{t('completed')}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <i className="ri-sword-line text-purple-500"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-white">{t('storePage')}</span>
                      <p className="text-xs text-gray-400">{t('storePageDesc')}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">{t('inProgress')}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                      <i className="ri-treasure-map-line text-pink-500"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-white">{t('gameStoryTranslation')}</span>
                      <p className="text-xs text-gray-400">{t('gameStoryDesc')}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">{t('pending')}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center text-indigo-400 hover:text-purple-400 transition-colors cursor-pointer"
            >
              {t('contactUs')}
              <i className="ri-arrow-right-line ri-sm ml-2"></i>
            </button>
          </div>

          {/* Feature 3 */}
          <div className="bg-gray-900 rounded-xl p-8 hover:bg-gray-800 transition-all duration-300 hover:scale-105">
            <h4 className="text-2xl font-bold mb-4 text-white">{t('feature3Title')}</h4>
            <p className="text-gray-300 mb-6">
              {t('feature3Description')}
            </p>

            <div className="space-y-6">
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex flex-col items-center">
                  <div className="grid grid-cols-4 gap-8 mb-8">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-blue-500/10 rounded-xl flex items-center justify-center mb-2">
                        <img
                          src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/79b383796935a37437f2f6cf5c9a452c.jfif"
                          alt="Unity Logo"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <span className="text-sm text-gray-300">Unity</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-purple-500/10 rounded-xl flex items-center justify-center mb-2">
                        <img
                          src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/c2156d07c7604cc9b90195dbf06e24f4.png"
                          alt="Unreal Logo"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <span className="text-sm text-gray-300">Unreal</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-green-500/10 rounded-xl flex items-center justify-center mb-2">
                        <img
                          src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/4dc41f5e7dea12391641e6289e4db181.png"
                          alt="Godot Logo"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <span className="text-sm text-gray-300">Godot</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-600/10 rounded-xl flex items-center justify-center mb-2">
                        <i className="ri-more-2-fill text-gray-400 ri-2x"></i>
                      </div>
                      <span className="text-sm text-gray-300">{t('more')}</span>
                    </div>
                  </div>

                  {/* Connection lines from engines to Flidge */}
                  <div className="relative w-full mb-4">
                    <svg className="w-full h-12" viewBox="0 0 400 48" fill="none">
                      {/* Lines from each engine to center */}
                      <path d="M50 0 L200 40" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M133 0 L200 40" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M267 0 L200 40" stroke="#10B981" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M350 0 L200 40" stroke="#6B7280" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />

                      {/* Arrow indicators */}
                      <polygon points="195,35 205,35 200,45" fill="#3B82F6" className="animate-pulse" />
                    </svg>
                  </div>

                  <div className="relative w-full h-20 mb-4">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                      <img
                        src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/e45d371f2489011ce3fc62e06fc363e4.png"
                        alt={t('flidgeLogo')}
                        className="h-8 w-auto"
                      />
                    </div>
                  </div>

                  {/* Connection lines from Flidge to formats */}
                  <div className="relative w-full mb-4">
                    <svg className="w-full h-12" viewBox="0 0 400 48" fill="none">
                      {/* Lines from center to each format */}
                      <path d="M200 8 L50 48" stroke="#EAB308" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M200 8 L133 48" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M200 8 L267 48" stroke="#10B981" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />
                      <path d="M200 8 L350 48" stroke="#6B7280" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse" />

                      {/* Arrow indicators */}
                      <polygon points="45,43 55,43 50,53" fill="#EAB308" className="animate-pulse" />
                      <polygon points="128,43 138,43 133,53" fill="#3B82F6" className="animate-pulse" />
                      <polygon points="262,43 272,43 267,53" fill="#10B981" className="animate-pulse" />
                      <polygon points="345,43 355,43 350,53" fill="#6B7280" className="animate-pulse" />
                    </svg>
                  </div>

                  <div className="grid grid-cols-4 gap-8">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-yellow-500/10 rounded-xl flex items-center justify-center mb-2">
                        <i className="ri-file-excel-2-line text-yellow-500 ri-2x"></i>
                      </div>
                      <span className="text-sm text-gray-300">.csv</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-blue-500/10 rounded-xl flex items-center justify-center mb-2">
                        <i className="ri-file-code-line text-blue-500 ri-2x"></i>
                      </div>
                      <span className="text-sm text-gray-300">.json</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-green-500/10 rounded-xl flex items-center justify-center mb-2">
                        <i className="ri-file-excel-line text-green-500 ri-2x"></i>
                      </div>
                      <span className="text-sm text-gray-300">.xlsx</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-600/10 rounded-xl flex items-center justify-center mb-2">
                        <i className="ri-more-2-fill text-gray-400 ri-2x"></i>
                      </div>
                      <span className="text-sm text-gray-300">{t('more')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center text-indigo-400 hover:text-purple-400 transition-colors cursor-pointer"
            >
              {t('contactUs')}
              <i className="ri-arrow-right-line ri-sm ml-2"></i>
            </button>
          </div>

          {/* Feature 4 */}
          <div className="bg-gray-900 rounded-xl p-8 hover:bg-gray-800 transition-all duration-300 hover:scale-105">
            <h4 className="text-2xl font-bold mb-4 text-white">{t('feature4Title')}</h4>
            <p className="text-gray-300 mb-6">
              {t('feature4Description')}
            </p>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <i className="ri-book-2-line text-indigo-500"></i>
                  </div>
                  <h5 className="font-medium text-white">{t('professionalTerminologyDatabase')}</h5>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{t('cheese')}</span>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <span className="text-sm text-white">ハメ技</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">{t('fightingGamesJapanese')}</span>
                </div>

                <div className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{t('feeding')}</span>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <span className="text-sm text-white">헌납</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">{t('mobaGamesKorean')}</span>
                </div>

                <div className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{t('rush')}</span>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <span className="text-sm text-white">зерг</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">{t('starcraftRussian')}</span>
                </div>

                <div className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">{t('balance')}</span>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <span className="text-sm text-white">霸体</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">{t('actionGamesChinese')}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center text-indigo-400 hover:text-purple-400 transition-colors cursor-pointer"
            >
              {t('contactUs')}
              <i className="ri-arrow-right-line ri-sm ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
